import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router";

import {
  ReportStatuses,
  REPORT_STATUSES,
} from "../../../constants/reportStatuses";
import MODALS from "../../../constants/modals";

import { useAppDispatch } from "../../../hooks/redux";
import { openModal } from "../../../features/modals";
import moment from "moment";
import axios from "axios";

import { Stack, Grid, Typography, Button, Chip } from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import { MaterialReactTable, MRT_ColumnDef } from "material-react-table";
import { IDailySheetReportResponse } from "../../../types/dailySheetReport";

import _ from "lodash";

const DailySheets: React.FunctionComponent<{}> = () => {
  const { clinic } = useParams<{ clinic: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState(true);
  const [reports, setReports] = useState<IDailySheetReportResponse[]>([]);

  const loadData = useCallback(() => {
    setIsLoading(true);
    axios
      .get<IDailySheetReportResponse[]>(`/daily-sheets/${clinic}`)
      .then((res) => {
        setReports(res.data);
      })
      .catch((err) => {
        console.log("error:", err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [clinic]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const columns = useMemo<MRT_ColumnDef<IDailySheetReportResponse>[]>(
    () => [
      {
        accessorKey: "date",
        header: "Datums",
        type: "date",
        Cell(props) {
          return moment(props.row.original.date).format("L");
        },
        enableColumnFilter: false,
      },
      {
        accessorKey: "status",
        header: "Status",
        Cell(props) {
          return (
            <Chip
              label={REPORT_STATUSES[props.row.original.status].label}
              variant={
                props.row.original.status === ReportStatuses.Opened
                  ? "filled"
                  : "outlined"
              }
              size="small"
              color={
                props.row.original.status === ReportStatuses.Opened
                  ? "success"
                  : "default"
              }
            />
          );
        },
        filterSelectOptions: _.map(REPORT_STATUSES, (status, key) => {
          return {
            text: status.label,
            value: key,
          };
        }),
        filterVariant: "select",
      },
      {
        accessorKey: "created",
        header: "Atvērta",
        type: "date",
        Cell(props) {
          return moment(props.row.original.created).format("L LT");
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "closed",
        header: "Aizvērta",
        type: "date",
        Cell(props) {
          return props.row.original.closed
            ? moment(props.row.original.closed).format("L LT")
            : "";
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
    ],
    []
  );

  return (
    <Stack spacing={2}>
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <Typography variant="h4">Dienas lapas</Typography>
        </Grid>
        <Grid item>
          <Button
            startIcon={<AddIcon />}
            disabled={!Boolean(clinic)}
            onClick={() => {
              if (clinic) {
                dispatch(
                  openModal({
                    type: MODALS.DailySheetReport,
                    clinic,
                    callback: (reportId) => {
                      navigate(`/${clinic}/daily-sheets/${reportId}`);
                    },
                  })
                );
              }
            }}
          >
            Izveidot jaunu
          </Button>
        </Grid>
      </Grid>
      <MaterialReactTable
        columns={columns}
        enableColumnActions={false}
        data={reports}
        getRowId={(row) => row._id}
        enableGlobalFilter={false}
        initialState={{
          sorting: [
            {
              id: "status",
              desc: true,
            },
            {
              id: "date",
              desc: true,
            },
          ],
        }}
        state={{
          isLoading,
        }}
        muiTableBodyRowProps={(props) => ({
          onClick: () => {
            navigate(`/${clinic}/daily-sheets/${props.row.original._id}`);
          },
          style: { cursor: "pointer" },
        })}
      />
    </Stack>
  );
};

export default DailySheets;
